import React from 'react';

import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import styles from './VolunteerOpportunities.module.css';
import {
  getVolunteerOpportunities,
  signupForVolunteerRole,
} from 'api/volunteerApi';
import { toast } from 'react-toastify';
import Loading from 'components/common/Loading';
import CustomButton from 'components/common/CustomButton';
import CustomTextarea from 'components/common/CustomTextarea';

// const VolunteerOpportunities = () => {
//   const [filters, setFilters] = useState({
//     date: '',
//     role: '',
//     points: '',
//     availability: '',
//   });

const VolunteerOpportunities = () => {
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [expandedMeets, setExpandedMeets] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  // Signup modal state
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');
  const [notes, setNotes] = useState('');

  const clubId = searchParams.get('clubId');

  const transformApiData = useCallback((apiData) => {
    const meetsMap = {};

    apiData.forEach((opp) => {
      const meetDateTime = new Date(opp.meetDate);
      const meetTime = meetDateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      if (!meetsMap[opp.meetID]) {
        meetsMap[opp.meetID] = {
          id: opp.meetID,
          name: opp.meetName,
          date: formatDate(opp.meetDate),
          location: opp.meetLocation,
          time: meetTime, // Extract time from meetDate
          urgency: getUrgencyLevel(opp.staffingPercentage),
          urgencyLabel: getUrgencyLabel(opp.staffingPercentage),
          opportunities: [],
        };
      }

      meetsMap[opp.meetID].opportunities.push({
        meetVolunteerRequirementID: opp.meetVolunteerRequirementID,
        role: opp.roleName,
        roles: [
          {
            volunteerRoleID: opp.volunteerRoleID,
            roleName: opp.roleName,
            roleDescription: opp.roleDescription,
            pointsAwarded: opp.pointsAwarded,
            numberRequired: opp.numberRequired,
            isFullyStaffed: opp.spotsRemaining <= 0,
            numberRemaining: opp.spotsRemaining,
          },
        ],
        description: opp.roleDescription,
        points: `${opp.pointsAwarded} points`,
        time: meetTime,
        status: getStatus(opp.spotsRemaining, opp.numberRequired),
        statusLabel: getStatusLabel(opp.spotsRemaining, opp.numberRequired),
        filled: opp.numberRequired - opp.spotsRemaining,
        total: opp.numberRequired,
        isUserSignedUp: opp.isUserSignedUp,
      });
    });

    return Object.values(meetsMap);
  }, []);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        const { data } = await getVolunteerOpportunities(clubId);
        const transformedData = transformApiData(data);
        setOpportunities(transformedData);

        // Commenting this out so it does not expand the list on first load
        // if (transformedData.length > 0) {
        //   setExpandedMeets([transformedData[0].id]);
        // }
      } catch (err) {
        toast.error(err.message || 'Failed to load volunteer opportunities');
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, [clubId, transformApiData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getUrgencyLevel = (percentage) => {
    if (percentage < 50) return 'high';
    if (percentage < 80) return 'medium';
    return 'low';
  };

  const getUrgencyLabel = (percentage) => {
    if (percentage < 50) return 'High Priority';
    if (percentage < 80) return 'Some Needs';
    return 'Well Staffed';
  };

  const getStatus = (spotsRemaining, totalRequired) => {
    if (spotsRemaining <= 0) return 'full';
    if (spotsRemaining / totalRequired < 0.3) return 'urgent';
    return 'available';
  };

  const getStatusLabel = (spotsRemaining, totalRequired) => {
    if (spotsRemaining <= 0) return 'Fully Staffed';
    if (spotsRemaining / totalRequired < 0.3) return 'Urgently Needed';
    return `${spotsRemaining} Spot${spotsRemaining === 1 ? '' : 's'} Available`;
  };

  const calculateSummaryStats = () => {
    const stats = {
      totalOpportunities: 0,
      availableNow: 0,
      //urgentlyNeeded: 0,
      totalPoints: 0,
    };

    opportunities.forEach((meet) => {
      meet.opportunities.forEach((opp) => {
        stats.totalOpportunities++;
        if (opp.status !== 'full') stats.availableNow++;
        //if (opp.status === 'urgent') stats.urgentlyNeeded++;
        stats.totalPoints += parseInt(opp.points.match(/\d+/)[0]);
      });
    });

    return stats;
  };

  const stats = calculateSummaryStats();

  const toggleMeet = (meetId) => {
    setExpandedMeets((prev) =>
      prev.includes(meetId)
        ? prev.filter((id) => id !== meetId)
        : [...prev, meetId]
    );
  };

  // const handleFilterChange = (e) => {
  //   const { name, value } = e.target;
  //   setFilters((prev) => ({ ...prev, [name]: value }));
  // };

  // const clearFilters = () => {
  //   setFilters({
  //     date: '',
  //     role: '',
  //     points: '',
  //     availability: '',
  //   });
  // };

  const handleSignupClick = (opportunity, meet) => {
    if (!opportunity || !meet) {
      toast.error('Missing opportunity or meet data');
      return;
    }

    setSelectedOpportunity({
      ...opportunity,
      meetName: meet.name,
      meetDate: meet.date,
    });
    setShowSignupModal(true);
    setSignupError('');
    setSignupSuccess('');
    setNotes('');
  };

  const handleCloseModal = () => {
    setSelectedOpportunity(null);
    setShowSignupModal(false);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (
      !selectedOpportunity ||
      !selectedOpportunity.meetVolunteerRequirementID
    ) {
      setSignupError('Missing required information for signup');
      return;
    }

    setSignupLoading(true);

    try {
      await signupForVolunteerRole({
        meetVolunteerRequirementID:
          selectedOpportunity.meetVolunteerRequirementID,
        notes,
      });

      // Refresh the opportunities data
      const { data } = await getVolunteerOpportunities(clubId);
      setOpportunities(transformApiData(data));

      setSignupSuccess('Successfully signed up for volunteer role!');
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setSignupError(
        err.response?.data?.message || 'Failed to sign up for volunteer role.'
      );
    } finally {
      setSignupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <span className={styles.icon}>🔍</span>
            <h1>Find Volunteer Opportunities</h1>
          </div>
          <div className={styles.breadcrumb}>
            <a href="/athlete-results">My Volunteer Status</a>
            <span>→</span>
            <span>Find Opportunities</span>
          </div>
        </div>
      </div>

      {/* <div className={styles.filtersSection}>
        <div className={styles.filtersHeader}>
          <h2 className={styles.filtersTitle}>Filter Opportunities</h2>
          <span className={styles.clearFilters} onClick={clearFilters}>
            Clear All
          </span>
        </div>
        <div className={styles.filtersGrid}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Date Range</label>
            <select
              className={styles.filterSelect}
              name="date"
              value={filters.date}
              onChange={handleFilterChange}
            >
              <option value="">All upcoming</option>
              <option value="week">Next 7 days</option>
              <option value="month">Next 30 days</option>
              <option value="quarter">Next 3 months</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Role Type</label>
            <select
              className={styles.filterSelect}
              name="role"
              value={filters.role}
              onChange={handleFilterChange}
            >
              <option value="">All roles</option>
              <option value="EventSpecific">Event Specific</option>
              <option value="General">General</option>
              <option value="Setup">Setup & Cleanup</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Points Range</label>
            <select
              className={styles.filterSelect}
              name="points"
              value={filters.points}
              onChange={handleFilterChange}
            >
              <option value="">All points</option>
              <option value="1-2">1-2 points</option>
              <option value="3-4">3-4 points</option>
              <option value="5+">5+ points</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Availability</label>
            <select
              className={styles.filterSelect}
              name="availability"
              value={filters.availability}
              onChange={handleFilterChange}
            >
              <option value="">All opportunities</option>
              <option value="available">Available spots</option>
              <option value="urgent">Urgently needed</option>
            </select>
          </div>
        </div>
      </div> */}

      <div className={styles.summaryStats}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryNumber}>
            {stats.totalOpportunities}
          </span>
          <div className={styles.summaryLabel}>Total Opportunities</div>
        </div>
        <div className={`${styles.summaryCard} ${styles.available}`}>
          <span className={styles.summaryNumber}>{stats.availableNow}</span>
          <div className={styles.summaryLabel}>Available Now</div>
        </div>

        {/* <div className={`${styles.summaryCard} ${styles.urgent}`}>
          <span className={styles.summaryNumber}>{stats.urgentlyNeeded}</span>
          <div className={styles.summaryLabel}>Urgently Needed</div>
        </div> */}

        <div className={styles.summaryCard}>
          <span className={styles.summaryNumber}>{stats.totalPoints}</span>
          <div className={styles.summaryLabel}>Total Points Available</div>
        </div>
      </div>

      <div className={styles.opportunitiesSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.headerFlex}>
            <span>📅</span>
            <span>Volunteer Opportunities</span>
          </div>
        </div>
        <div className={styles.opportunitiesContent}>
          {opportunities.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No volunteer opportunities found matching your criteria.</p>
              {/* <button
                onClick={clearFilters}
                className={styles.clearFiltersButton}
              >
                Clear filters
              </button> */}
            </div>
          ) : (
            opportunities.map((meet) => (
              <div
                key={meet.id}
                className={`${styles.meetGroup} ${expandedMeets.includes(meet.id) ? styles.expanded : ''}`}
              >
                <div
                  className={`${styles.meetHeader} ${expandedMeets.includes(meet.id) ? styles.expanded : ''}`}
                  onClick={() => toggleMeet(meet.id)}
                >
                  <div className={styles.meetInfo}>
                    <div className={styles.meetDetails}>
                      <h3>{meet.name}</h3>
                      <div className={styles.meetMeta}>
                        <span>📅 {meet.date}</span>
                        <span>📍 {meet.location}</span>
                        <span>🕐 {meet.time}</span>
                      </div>
                    </div>
                    <div className={styles.meetStats}>
                      <span
                        className={`${styles.urgencyBadge} ${styles[meet.urgency]}`}
                      >
                        {meet.urgencyLabel}
                      </span>
                      <span className={styles.expandArrow}>
                        {expandedMeets.includes(meet.id) ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                </div>
                {expandedMeets.includes(meet.id) && (
                  <div className={styles.opportunitiesList}>
                    {meet.opportunities.map((opp, index) => (
                      <div key={index} className={styles.opportunityCard}>
                        <div className={styles.opportunityHeader}>
                          <div className={styles.roleInfo}>
                            <h4>{opp.role}</h4>
                            <div className={styles.roleDescription}>
                              {opp.description}
                            </div>
                            <div className={styles.roleMeta}>
                              <span>⭐ {opp.points}</span>
                              <span>🕐 {opp.time}</span>
                            </div>
                          </div>
                          <div className={styles.staffingInfo}>
                            <div
                              className={`${styles.staffingStatus} ${styles[opp.status]}`}
                            >
                              {opp.statusLabel}
                            </div>
                            <div className={styles.staffingBar}>
                              <div
                                className={`${styles.staffingFill} ${styles[opp.status]}`}
                                style={{
                                  width: `${(opp.filled / opp.total) * 100}%`,
                                }}
                              ></div>
                            </div>
                            <div className={styles.staffingText}>
                              {opp.filled} of {opp.total} filled
                            </div>
                          </div>
                        </div>
                        <div className={styles.opportunityActions}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={() => handleSignupClick(opp, meet)}
                            disabled={
                              opp.status === 'full' || opp.isUserSignedUp
                            }
                          >
                            <span>{opp.status === 'full' ? '✓' : '✚'}</span>
                            {opp.status === 'full'
                              ? 'Fully Staffed'
                              : opp.isUserSignedUp
                                ? 'Already Signed Up'
                                : 'Sign Up'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Signup Modal */}
      {showSignupModal && selectedOpportunity && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Confirm Volunteer Signup</h2>
              <button className={styles.closeBtn} onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.confirmationMessage}>
                <h3>{selectedOpportunity.role}</h3>
                <p>
                  You&apos;re signing up to volunteer for this role at the{' '}
                  {selectedOpportunity.meetName} on{' '}
                  {new Date(selectedOpportunity.meetDate).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }
                  )}
                  .
                </p>
                <p>
                  You&apos;ll earn {selectedOpportunity.points} for completing
                  this role
                </p>
              </div>

              <form onSubmit={handleSignupSubmit}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Notes (Optional)</label>
                  <CustomTextarea
                    className={styles.formTextarea}
                    placeholder="Any relevant information, availability notes, or questions..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>

                {signupError && (
                  <div className={styles.errorMsg}>{signupError}</div>
                )}
                {signupSuccess && (
                  <div className={styles.successMsg}>{signupSuccess}</div>
                )}

                <div className={styles.confirmationActions}>
                  <CustomButton
                    onClick={handleCloseModal}
                    disabled={signupLoading}
                    style={{
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      border: '1px solid #dee2e6',
                      height: '2.5rem',
                      padding: '0 1rem',
                      marginRight: '1rem',
                    }}
                  >
                    Cancel
                  </CustomButton>
                  <div onClick={handleSignupSubmit}>
                    <CustomButton
                      disabled={signupLoading}
                      style={{
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        height: '2.5rem',
                        padding: '0 1rem',
                        marginBottom: 0,
                      }}
                    >
                      {signupLoading ? (
                        <>
                          <span className={styles.spinner}></span>
                          Signing Up...
                        </>
                      ) : (
                        'Confirm Signup'
                      )}
                    </CustomButton>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerOpportunities;
